import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Drawer, Box, Typography, Stack, IconButton, Chip, Divider, Button, Alert, Skeleton, alpha, useTheme,
} from '@mui/material';
import { Close, CardMembership, OpenInNew, People } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import SerialNoDataGrid from '../ui/SerialNoDataGrid';
import {
  getPartnerPlanPriceDisplay,
  zoneLabels,
  type PartnerSubscriptionPlanRow,
} from '../../services/partnerSubscriptionPlanService';
import {
  PARTNER_PLAN_BILLING_INTERVALS,
  PARTNER_PLAN_TYPE_OPTIONS,
  PARTNER_PLAN_ALERT_SX,
  PARTNER_PLAN_NOTE_SX,
} from '../../constants/partnerSubscriptionPlanConfig';
import { listPartners, partnersUrlForSubscriptionPlan, type PartnerRow } from '../../services/partnerUserService';
import { formatEmail, formatPhone } from '../../utils/contactMask';
import { useMaskContactSetting } from '../../hooks/useMaskContactSetting';

interface Props {
  open: boolean;
  onClose: () => void;
  plan: PartnerSubscriptionPlanRow | null;
}

function planDetailLabel(value: string, options: ReadonlyArray<{ value: string; label: string }>) {
  return options.find((o) => o.value === value)?.label || value;
}

export default function PartnerPlanSubscribersDrawer({ open, onClose, plan }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { maskEnabled } = useMaskContactSetting();

  const entityType = plan?.partnerType && plan.partnerType !== 'all' ? plan.partnerType : undefined;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['plan-subscribers', plan?._id, entityType],
    queryFn: () => listPartners({
      entityType,
      partnerSubscriptionPlanId: plan!._id,
      limit: 100,
      page: 1,
    }),
    enabled: open && !!plan?._id,
  });

  const rows = data?.data || [];
  const total = data?.total ?? rows.length;

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'name',
      headerName: 'Partner',
      flex: 1,
      minWidth: 140,
      valueGetter: (_v, row) => `${(row as PartnerRow).firstName} ${(row as PartnerRow).lastName || ''}`.trim(),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 170,
      valueGetter: (_v, row) => formatEmail((row as PartnerRow).email, maskEnabled),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 130,
      valueGetter: (_v, row) => formatPhone((row as PartnerRow).phone, maskEnabled),
    },
    {
      field: 'userType',
      headerName: 'Type',
      width: 120,
      valueGetter: (_v, row) => planDetailLabel((row as PartnerRow).userType, PARTNER_PLAN_TYPE_OPTIONS),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={(row as PartnerRow).isActive ? 'Active' : 'Inactive'}
          color={(row as PartnerRow).isActive ? 'success' : 'default'}
        />
      ),
    },
  ], [maskEnabled]);

  if (!plan) return null;

  const partnersUrl = partnersUrlForSubscriptionPlan(plan);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520, md: 680 } } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{
            p: 2.5,
            borderBottom: 1,
            borderColor: 'divider',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.warning.main, 0.06)} 100%)`,
          }}
        >
          <Box minWidth={0}>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <CardMembership color="primary" />
              <Typography variant="h6" fontWeight={800}>Subscribed partners</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" noWrap>
              {plan.name} · <Typography component="span" variant="body2" fontFamily="monospace">{plan.code}</Typography>
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close"><Close /></IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
          <Stack spacing={2}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.background.default, 0.6),
              }}
            >
              <Typography variant="overline" color="text.secondary" fontWeight={700}>
                Subscription plan
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ mt: 0.5 }}>
                {getPartnerPlanPriceDisplay(plan).summary}
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                <Chip size="small" label={planDetailLabel(String(plan.billingInterval), PARTNER_PLAN_BILLING_INTERVALS)} />
                <Chip size="small" label={`${plan.validityDays} days`} />
                <Chip size="small" label={planDetailLabel(plan.partnerType, PARTNER_PLAN_TYPE_OPTIONS)} />
                <Chip
                  size="small"
                  color={plan.isActive ? 'success' : 'default'}
                  label={plan.isActive ? 'Active plan' : 'Inactive plan'}
                />
              </Stack>
              {zoneLabels(plan).length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {zoneLabels(plan).map((label) => (
                    <Chip key={label} size="small" variant="outlined" label={label} />
                  ))}
                </Stack>
              )}
              {plan.description && (
                <Typography variant="body2" sx={{ ...PARTNER_PLAN_NOTE_SX, mt: 1.5 }}>
                  {plan.description}
                </Typography>
              )}
            </Box>

            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <People color="warning" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={800}>
                  {total} partner{total === 1 ? '' : 's'} subscribed
                </Typography>
              </Stack>
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNew fontSize="small" />}
                component={RouterLink}
                to={partnersUrl}
                onClick={onClose}
              >
                Open in Partners
              </Button>
            </Stack>

            {isError && (
              <Alert severity="error">Could not load subscribed partners. Try again from the Partners page.</Alert>
            )}

            {isLoading && (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={48} />
                <Skeleton variant="rounded" height={240} />
              </Stack>
            )}

            {!isLoading && !isError && rows.length === 0 && (
              <Alert severity="info" icon={<People />} sx={PARTNER_PLAN_ALERT_SX}>
                No partners are subscribed to this plan yet. Partners pick a plan during onboarding or registration.
              </Alert>
            )}

            {!isLoading && rows.length > 0 && (
              <Box sx={{ height: 360, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <SerialNoDataGrid
                  rows={rows}
                  columns={columns}
                  getRowId={(r) => r._id}
                  disableRowSelectionOnClick
                  onRowClick={(params) => {
                    navigate(`/partners/${params.row._id}`, { state: { from: '/partners/subscription-plans' } });
                    onClose();
                  }}
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  sx={{ border: 0, cursor: 'pointer' }}
                />
              </Box>
            )}
          </Stack>
        </Box>

        <Divider />
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ p: 2 }}>
          <Button onClick={onClose}>Close</Button>
          <Button variant="contained" component={RouterLink} to={partnersUrl} onClick={onClose}>
            View all in Partners
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
